// Locale-aware labels for a Sanity article's taxonomy codes. The CMS stores
// `topics` / `cities` as language-neutral codes (identical across the EN/FR/RU
// versions of an article), so the display text lives here. Reads the resource
// JSON directly, like city-content.ts, so the framework-free <Head> builders
// can call it without a `t()`.

import { cities } from '@/lib/events-data';
import type { Locale } from '@todo/core/i18n/locales';
import en from '@/lib/i18n/resources/en.json';
import fr from '@/lib/i18n/resources/fr.json';
import ru from '@/lib/i18n/resources/ru.json';

type ArticlesResource = { articles: { topics: Record<string, string> } };

const resources: Record<Locale, ArticlesResource> = { en, fr, ru };

/**
 * Localized labels for an article's topic codes, in authored order (the first
 * is the main topic). Unknown codes are dropped rather than shown raw.
 */
export function articleTopicLabels(topics: string[] | null | undefined, locale: Locale): string[] {
  const labels = resources[locale].articles.topics;
  return (topics ?? []).flatMap((topic) => (labels[topic] ? [labels[topic]] : []));
}

/**
 * The known cities among an article's city codes, in authored order. Codes
 * that don't match a city in events-data (a typo, or a city added in the CMS
 * before the app knows it) are dropped, so nothing links to an unexported page.
 */
export function articleCities(cityIds: string[] | null | undefined): { id: string; name: string }[] {
  return (cityIds ?? []).flatMap((id) => {
    const city = cities.find((c) => c.id === id);
    return city ? [{ id: city.id, name: city.name }] : [];
  });
}

/** Past this an alt text is almost certainly pasted body copy, not a description. */
const MAX_IMAGE_ALT_LENGTH = 125;

/**
 * Alt text for an article's main image: the CMS `mainImage.alt`, or the title
 * when it's missing or implausibly long (one live article had its whole body
 * pasted into the alt field). The Studio warns past the same length.
 */
export function articleImageAlt(article: { title: string; mainImageAlt: string | null }): string {
  const alt = article.mainImageAlt?.trim();
  return alt && alt.length <= MAX_IMAGE_ALT_LENGTH ? alt : article.title;
}
